import ChevronLeft from '@/assets/icons/chevron-left.svg?react'
import StarIcon from '@/assets/icons/star/full.svg?react'
import Loading from '@/components/Loading/Loading'
import SkipLink from '@/components/SkipLink'
import Navbar from '@/layout/Navbar'
import useGrubhubValidToken from '@/lib/food-grubhub/hooks/useGrubhubValidToken'
import useMenuItemCategoryListing from '@/lib/food-grubhub/hooks/useMenuItemCategoryListing'
import useRestaurantInformationDetail from '@/lib/food-grubhub/hooks/useRestaurantInformationDetail'
import {useGrubhubUserAddress} from '@/lib/food-grubhub/store'
import HeadTags from '@/lib/head/HeadTags'
import {FoodGrubhubDetailRoute, FoodGrubhubRoute} from '@/routeRegistry'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import useParam from '@/utils/router/useParam'
import ReadMoreList from '@/views/FoodGrubhubRestaurantDetail/components/ReadMoreList'

import FoodGrubhubMenuItemListing from './components/FoodGrubhubMenuItemListing/FoodGrubhubMenuItemListing'
import FoodGrubhubMenuItemSearch from './components/FoodGrubhubMenuItemSearch/FoodGrubhubMenuItemSearch'
import style from './FoodGrubhubRestaurantDetail.module.scss'

export default function FoodGrubhubRestaurantDetail() {
  const [storageUserLocation, _setStorageUserLocation] = useGrubhubUserAddress()
  const [restaurantId] = useParam(FoodGrubhubDetailRoute.id, 'restaurantId')
  const {search} = FoodGrubhubDetailRoute.useSearch()
  const router = useRouter()

  const storageToken = useGrubhubValidToken()

  const restaurantInforamtionQuery = useMemo(
    () => ({
      restaurantId,
      hideChoiceCategories: true,
      version: 4,
      variationId: 'rtpFreeItems',
      orderType: '',
      hideUnavailableMenuItems: true,
      hideMenuItems: true,
      locationMode: 'delivery',
      location: `POINT(${storageUserLocation.longitude} ${storageUserLocation.latitude})`,
    }),
    [restaurantId, storageUserLocation.longitude, storageUserLocation.latitude],
  )
  const {data: restaurantDetail, isLoading: restaurantDetailLoading} =
    useRestaurantInformationDetail(restaurantInforamtionQuery, storageToken)

  const menuItemCategoryListQuery = useMemo(
    () => ({
      orderType: 'STANDARD',
      platform: 'WEB',
      enhancedFeed: true,
      weightedItemDataIncluded: true,
      location: `POINT(${storageUserLocation.longitude} ${storageUserLocation.latitude})`,
      viewport: 'dWeb',
      restaurantId,
    }),
    [restaurantId, storageUserLocation.longitude, storageUserLocation.latitude],
  )
  const {data: menuItemCategoryListData} = useMenuItemCategoryListing(
    menuItemCategoryListQuery,
    storageToken,
  )

  const filteredNoneIdCategory = menuItemCategoryListData?.object.data.enhanced_feed.filter(
    item => item.id !== 'None' && !isNaN(Number(item.id)),
  )

  const getGoBackHref = useCallback(
    (lng: number, lat: number, address: string) => {
      return router.buildLocation({
        to: FoodGrubhubRoute.fullPath,
        search: {
          lng,
          lat,
          address,
        },
      }).href
    },
    [router],
  )

  //Prefix
  const restaurantInfo = restaurantDetail?.restaurant
  const restaurantAvaibility = restaurantDetail?.restaurant_availability

  const restaurantAddressFormat = `${restaurantInfo?.address?.street_address}, ${restaurantInfo?.address?.locality}, ${restaurantInfo?.address?.zip}`

  const fullyLoadding = restaurantDetailLoading

  return (
    <div>
      <HeadTags title={restaurantDetail?.restaurant.name ?? 'Restaurant'} />
      <SkipLink title='Skip to main content' to='#main-content' />
      <Navbar withSearch variant='dark' />
      <main className={style.PageContainer} {...skipTargetProps('main-content')}>
        {fullyLoadding ? (
          <Loading label='Loading restaurant detail page' />
        ) : (
          <>
            <section className={style.PageHeader}>
              <div className={style.PageHeaderContent}>
                <Link
                  to={getGoBackHref(
                    storageUserLocation.longitude ?? 0,
                    storageUserLocation.latitude ?? 0,
                    storageUserLocation.address ?? '',
                  )}
                  aria-label='Go back to store search detail page'
                  className={style.ButtonBack}
                >
                  <ChevronLeft className={style.ButtonBackIcon} />
                  <p className={style.ButtonBackTitleMobile}>Go back</p>
                </Link>
                <div className={style.HeaderImageContainer}>
                  <div className={style.HeaderImageContent}>
                    <img
                      src={restaurantInfo?.logo ?? ''}
                      alt=''
                      loading='lazy'
                      className={style.HeaderImage}
                    />
                  </div>
                  <div className={style.HeaderImageInfoMobile}>
                    <h1 className={style.Heading}>{restaurantInfo?.name}</h1>
                    <ReadMoreList
                      items={restaurantInfo?.cuisines ?? []}
                      maxVisible={10}
                      viewMoreTitle='Show more'
                      viewLessTitle='Show less'
                    />
                  </div>
                </div>
                <div className={style.HeaderInfoContainer}>
                  <div className={style.HeaderInfoDesktop}>
                    <h1 className={style.Heading}>{restaurantInfo?.name}</h1>
                    <ReadMoreList
                      items={restaurantInfo?.cuisines ?? []}
                      maxVisible={10}
                      viewMoreTitle='Show more'
                      viewLessTitle='Show less'
                    />
                  </div>
                  <div className={style.HeaderInfoDetail}>
                    <div className={style.BreakLine}></div>
                    {restaurantInfo?.rating && (
                      <div className={style.RatingContainer}>
                        <VisuallyHidden>{`Rating: ${restaurantInfo.rating.rating_value} out of 5 stars based on ${restaurantInfo.rating.rating_value} reviews`}</VisuallyHidden>
                        <div className={style.RatingNumber} aria-hidden='true'>
                          {restaurantInfo.rating.rating_value}
                        </div>
                        <StarIcon />
                        <div className={style.RatingNumber} aria-hidden='true'>
                          {'\u0028'}
                          {restaurantInfo.rating.rating_count} reviews
                          {'\u0029'}
                        </div>
                      </div>
                    )}
                    <div className={style.Address}>
                      <VisuallyHidden>Store address: </VisuallyHidden> {restaurantAddressFormat}
                    </div>
                    <div className={style.DeliveryInfoContainer}>
                      <div className={style.DeliveryItem}>
                        <VisuallyHidden>Delivery distance: </VisuallyHidden>
                        {`\u003C ${restaurantAvaibility?.decimal_distance_in_miles} mi`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            {search !== '' ? (
              <FoodGrubhubMenuItemSearch
                restaurantDetail={restaurantDetail}
                restaurantId={restaurantId}
                operationId={menuItemCategoryListData?.object.operation_id ?? ''}
              />
            ) : (
              <FoodGrubhubMenuItemListing
                menuItemCategoryList={filteredNoneIdCategory ?? []}
                restaurantDetail={restaurantDetail}
                restaurantId={restaurantId}
                operationId={menuItemCategoryListData?.object.operation_id ?? ''}
              />
            )}
          </>
        )}
      </main>
    </div>
  )
}
